import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Box, Button, Link, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Body from '../components/Body';
import Image from '../components/Image';
import Section from '../components/Section';
import LoginDialog from '../forms/LoginDialog';
import LoggedInDialog from '../forms/LoggedInDialog';
import { useAuth } from '../data/auth/token';
import { track } from '../helpers/mixpanel';

const ctaSx = {
  mb: 1,
  backgroundAttachment: 'fixed',
  backgroundImage: 'repeating-radial-gradient(circle at 100px -400px, #76451F66 0, transparent 800px), repeating-linear-gradient(#be8a6144, #be8a6122)',
};

const outlinedSx = {
  mb: 1,
  backgroundAttachment: 'fixed',
  backgroundImage: 'repeating-radial-gradient(circle at 0 0, #230e11 0, transparent 600px), repeating-linear-gradient(#4a362b88, #4a362b44)',
};

type PathKey = 'match' | 'hide' | 'data';

// End of 30 June 2026, Swedish time. After this the page drops the summer
// offer and switches to reclaim-your-account messaging.
const CLOSING_AT = new Date('2026-07-01T00:00:00+02:00');

/**
 * Public static info page announcing that the Relate Date app closes on
 * 30 June 2026. Reached from SMS and in-app chat links at /sunset.
 * No login required. The visitor picks one of three paths (keep being
 * matched / hide profile / delete profile) and only that path's compact
 * card is shown, so each view has a single goal. The match path routes
 * through the training funnel (/training) rather than the long membership
 * application; declining the offer leads to a login dialog (same as the
 * membership page) that continues into the membership application flow.
 * After 30 June the summer offer is replaced by reclaim-your-account
 * messaging with the same training CTA and a direct login link.
 */
export default function SunsetInfo() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const [path, setPath] = useState<PathKey>('match');
  const [declined, setDeclined] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showLoggedIn, setShowLoggedIn] = useState(false);
  const [searchParams] = useSearchParams();
  // SMS links may carry the recipient's sunset token — forward it so the
  // delete flow works without login.
  const token = searchParams.get('token');
  // ?closed=1 forces the post-June-30 render for testing.
  const closed = Date.now() >= CLOSING_AT.getTime() || searchParams.get('closed') === '1';

  const choices: { key: PathKey, label: string }[] = [
    { key: 'match', label: t('sunset.info.choice_match') },
    { key: 'hide', label: t('sunset.info.choice_hide') },
    { key: 'data', label: t('sunset.info.choice_data') },
  ];

  const selectPath = useCallback((key: PathKey) => {
    setPath(key);
    setDeclined(false);
    track('sunset_info_path_selected', { path: key });
  }, []);

  const handleTraining = useCallback(() => {
    track('sunset_info_training_clicked');
    navigate('/training');
  }, [navigate]);

  const handleDecline = useCallback(() => {
    track('sunset_info_offer_declined');
    setDeclined(true);
  }, []);

  const handleMatchableLogin = useCallback(() => {
    track('sunset_info_login_clicked', { source: 'declined_offer' });
    if (!auth?.user) {
      // Open login modal.
      setShowLogin(true);
    } else if (auth?.user?.appliedAt) {
      track('already_applied');
      navigate('/');
    } else if (auth?.user && !auth?.user?.firstName && !auth?.user?.lastName) {
      // Skip profile selection if none exist already.
      navigate('/application?service=member');
    } else {
      // Start profile form if logged in.
      setShowLoggedIn(true);
    }
  }, [auth, navigate]);

  const handleHide = useCallback(() => {
    track('sunset_info_hide_clicked');
    navigate(token ? `/hide?token=${encodeURIComponent(token)}` : '/hide');
  }, [navigate, token]);

  const handleDelete = useCallback(() => {
    track('sunset_info_delete_clicked');
    navigate(token ? `/delete?token=${encodeURIComponent(token)}` : '/delete');
  }, [navigate, token]);

  return (
    <Body>
      <Helmet>
        <title>Relate | {t('sunset.info.page_title')}</title>
      </Helmet>
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <Image src="/logo.png" sx={{ width: 65 }} />
      </Box>
      <Box>
        <Section sx={{ textAlign: 'center' }}>
          <Typography component="h1" variant="h1" sx={{ mb: 4 }}>
            {closed ? t('sunset.info.title_closed') : t('sunset.info.title')}
          </Typography>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
            {t('sunset.info.intro')}
          </Typography>
        </Section>
        <Section>
          <Typography component="h3" variant="h3" textAlign="center" sx={{ mb: 2 }}>
            {t('sunset.info.choice_title')}
          </Typography>
          {choices.map((choice) => (
            <Button
              key={choice.key}
              fullWidth
              size="large"
              variant={path === choice.key ? 'contained' : 'outlined'}
              onClick={() => selectPath(choice.key)}
              sx={path === choice.key ? ctaSx : outlinedSx}
            >
              {choice.label}
            </Button>
          ))}
        </Section>
        {path === 'match' && !declined && (
          <Section highlight>
            <Typography component="h2" variant="h2" textAlign="center">
              {closed ? t('sunset.info.closed_title') : t('sunset.info.summer_title')}
            </Typography>
            {closed ? (
              <Typography variant="body1" textAlign="center" sx={{ mb: 3, whiteSpace: 'pre-line' }}>
                {t('sunset.info.closed_body')}
              </Typography>
            ) : (
              <>
                <Typography variant="body1" textAlign="center" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
                  {t('sunset.info.network_body')}
                </Typography>
                <Typography variant="body1" textAlign="center" sx={{ mb: 3, whiteSpace: 'pre-line' }}>
                  {t('sunset.info.summer_body')}
                </Typography>
              </>
            )}
            <Button
              fullWidth
              size="large"
              variant="contained"
              onClick={handleTraining}
              sx={ctaSx}
            >
              {t('sunset.info.summer_cta')}
            </Button>
            <Link
              component="button"
              onClick={closed ? handleMatchableLogin : handleDecline}
              sx={{ mt: 2, color: '#fff', textAlign: 'center' }}
            >
              {closed ? t('sunset.info.closed_login_link') : t('sunset.info.summer_decline_link')}
            </Link>
          </Section>
        )}
        {path === 'match' && declined && (
          <Section highlight>
            <Typography component="h2" variant="h2" textAlign="center">
              {t('sunset.info.decline_title')}
            </Typography>
            <Typography variant="body1" textAlign="center" sx={{ mb: 3, whiteSpace: 'pre-line' }}>
              {t('sunset.info.decline_body')}
            </Typography>
            <Button
              fullWidth
              size="large"
              variant="contained"
              onClick={handleMatchableLogin}
              sx={ctaSx}
            >
              {t('sunset.info.decline_cta')}
            </Button>
          </Section>
        )}
        {path === 'hide' && (
          <Section highlight>
            <Typography component="h2" variant="h2" textAlign="center">
              {t('sunset.info.hide_title')}
            </Typography>
            <Typography variant="body1" textAlign="center" sx={{ mb: 3, whiteSpace: 'pre-line' }}>
              {t('sunset.info.hide_body')}
            </Typography>
            <Button
              fullWidth
              size="large"
              variant="contained"
              onClick={handleHide}
              sx={ctaSx}
            >
              {t('sunset.info.hide_cta')}
            </Button>
          </Section>
        )}
        {path === 'data' && (
          <Section highlight>
            <Typography component="h2" variant="h2" textAlign="center">
              {t('sunset.info.data_title')}
            </Typography>
            <Typography variant="body1" textAlign="center" sx={{ mb: 3, whiteSpace: 'pre-line' }}>
              {t('sunset.info.data_body')}
            </Typography>
            <Link
              component="button"
              onClick={handleDelete}
              sx={{ color: '#fff', textAlign: 'center' }}
            >
              {t('sunset.info.data_delete_link')}
            </Link>
          </Section>
        )}
        <Section sx={{ alignItems: 'center' }}>
          <Typography variant="body2" textAlign="center" sx={{ mb: 4 }}>
            {t('sunset.info.footer')}
          </Typography>
          <Image src="/logo.png" sx={{ width: 84 }} />
          <Typography variant="caption">
            {t('slogan')}
          </Typography>
        </Section>
        <Section />
        {showLogin && (
          <LoginDialog
            open
            handleClose={() => setShowLogin(false)}
            onLoggedIn={(user) => {
              if (user?.appliedAt) {
                track('already_applied');
                navigate('/');
              } else if (user && !user?.firstName && !user?.lastName) {
                // Skip profile selection if none exist already.
                navigate('/application?service=member');
              } else {
                setShowLoggedIn(true);
              }
            }}
          />
        )}
        {showLoggedIn && (
          <LoggedInDialog
            open
            handleClose={() => setShowLoggedIn(false)}
            nextPath="/application?service=member"
          />
        )}
      </Box>
    </Body>
  );
}
